<template>
  <div class="streamed-wages-list">
    <sme-alert v-if="error" level="danger" class="mb-3">{{ error }}</sme-alert>
    <sme-alert v-if="dataTruncated" level="warning" class="mb-3">
      {{ $t('transferredWages.dataTruncatedAlert') }}
    </sme-alert>
    <page-list-header>
      <template #left>
        <filter-dropdown v-model="filters" :filters="filterItems">
          <inline-search v-if="!employeeId" v-model="searchText" @search="onSearch" />
        </filter-dropdown>
      </template>
      <template #right>
        <b-form-group label-cols="auto" :label="$t('common.perPageLabel')" class="mb-0">
          <b-form-select v-model="perPage" :options="STREAM_PER_PAGE_OPTIONS" />
        </b-form-group>
        <export-button
          :export-data="filteredTransfers"
          :export-file-rows="exportFileRows"
          :export-headers="exportHeaders"
          file-name="streamed-wages"
        />
      </template>
    </page-list-header>
    <component :is="componentType" class="mb-3">
      <app-loading :loading="loading" />

      <b-table
        v-if="!loading"
        :items="transfers"
        :fields="fieldDefinitions"
        filter="''"
        :filter-function="filterFunction"
        :current-page="currentPage"
        :per-page="perPage"
        class="mb-0"
        table-class="table-minimal"
        :tfoot-class="!transfers?.length ? 'd-none' : ''"
        primary-key="transfer_id"
        foot-clone
        no-footer-sorting
        responsive
        show-empty
        @filtered="update"
      >
        <template v-slot:cell(full_name)="data">
          <router-link :to="{ name: 'employee', params: { employeeId: data?.item?.employee_id } }">
            {{ data?.item?.full_name }}
          </router-link>
        </template>
        <template v-slot:foot()>&nbsp;</template>
        <template v-slot:foot(net_amount)>{{ totalNetAmount }}</template>
      </b-table>
    </component>

    <b-pagination
      v-model="currentPage"
      :total-rows="filteredTransfers.length"
      :per-page="perPage"
      class="justify-content-end"
    />
  </div>
</template>

<script setup>
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import sumBy from 'lodash/sumBy';
import moment from 'moment';
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import i18n from '@/i18n';
import { CONTROL_TYPES } from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import ExportButton from '@/components/ExportButton.vue';
import FilterDropdown from '@/components/FilterDropdown.vue';
import InlineSearch from '@/components/InlineSearch.vue';
import PageListHeader from '@/components/PageListHeader.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import {
  STREAM_FILTER_KEYS,
  STREAM_SEARCH_FIELDS,
  STREAM_API_FILTER_KEYS,
  getBaseStreamFieldDefinitions,
  STREAM_PER_PAGE_OPTIONS,
} from '@/pages/streamed-wages/constants';
import usePayschedules from '@/state/composables/usePayschedules';
import { toCurrency } from '@/utils';
import { EMPLOYEE_PAY_TYPE } from '@/utils/Employee';
import debounceAsync from '@/utils/debounceAsync';
import useFeatureFlags from '@/composables/useFeatureFlags';

const { getPayschedules, payschedules } = usePayschedules();
const { isEnableShowPayschedulesOnStreamedWagesPage } = useFeatureFlags();

const STARTS_ON_DEFAULT_VALUE = moment().startOf('month').format('YYYY-MM-DD');
const ENDS_ON_DEFAULT_VALUE = moment().format('YYYY-MM-DD');

const props = defineProps({
  nested: {
    type: Boolean,
    default: false,
  },
  employeeId: {
    type: String,
  },
  filterKeys: {
    type: Array,
    default: () => Object.values(STREAM_FILTER_KEYS),
  },
  fieldKeys: {
    type: Array,
  },
});

const transfers = ref([]);
const filteredTransfers = ref([]);
const divisions = ref([]);
const employeeFilters = ref(null);
const loading = ref(true);
const error = ref('');
const initialized = ref(false);
const filters = ref({
  [STREAM_FILTER_KEYS.STARTS_ON]: STARTS_ON_DEFAULT_VALUE,
  [STREAM_FILTER_KEYS.ENDS_ON]: ENDS_ON_DEFAULT_VALUE,
});
const searchCriteria = ref('');
const searchText = ref('');
const currentPage = ref(1);
const perPage = ref(STREAM_PER_PAGE_OPTIONS[0]);

const hasSavingsWithdrawals = computed(() => transfers.value.some(transfer => transfer.properties.safestream));
const hasBankPayTransfers = computed(() => transfers.value.some(transfer => transfer.properties.autostream_shift));
const dataTruncated = computed(() => filteredTransfers.value.length >= 10000);

const fieldDefinitions = computed(() => {
  // Calculate the fields definitions based on the pulled transfers and company feature flags.
  // Use getBaseStreamFieldDefinitions() as the base and add the pay_schedule_name field if the company feature flag is enabled.
  const computedFieldDefinitions = getBaseStreamFieldDefinitions();

  if (isEnableShowPayschedulesOnStreamedWagesPage.value) {
    computedFieldDefinitions.push({
      key: 'pay_schedule_name',
      label: i18n.t('payschedules.returnedSalaries.payScheduleHeader'),
      sortable: true,
    });
  }
  if (hasSavingsWithdrawals.value || hasBankPayTransfers.value) {
    computedFieldDefinitions.push({
      key: 'type',
      label: i18n.t('absences.fields.type'),
      sortable: true,
      formatter: (_value, _key, item) => {
        if (item.properties.safestream) {
          return i18n.t('transferredWages.types.savingsWithdrawal');
        } else if (item.properties.autostream_shift) {
          return i18n.t('transferredWages.types.bankPay');
        } else {
          return i18n.t('transferredWages.types.transfer');
        }
      },
    });
  }

  return computedFieldDefinitions;
});

const exportHeaders = computed(() => fieldDefinitions.value.map(field => field.label));

const filterItems = computed(() =>
  [
    {
      key: STREAM_FILTER_KEYS.STARTS_ON,
      label: i18n.t('common.fromLabel'),
      type: CONTROL_TYPES.DATE,
      defaultValue: STARTS_ON_DEFAULT_VALUE,
      controlProps: {
        max: filters.value[STREAM_FILTER_KEYS.ENDS_ON] ? filters.value[STREAM_FILTER_KEYS.ENDS_ON] : null,
      },
    },
    {
      key: STREAM_FILTER_KEYS.ENDS_ON,
      label: i18n.t('common.toLabel'),
      type: CONTROL_TYPES.DATE,
      defaultValue: ENDS_ON_DEFAULT_VALUE,
      controlProps: {
        min: filters.value[STREAM_FILTER_KEYS.STARTS_ON] ? filters.value[STREAM_FILTER_KEYS.STARTS_ON] : null,
      },
    },
    {
      key: STREAM_FILTER_KEYS.SALARY_OR_HOURLY,
      label: i18n.t('employees.filters.payTypeLabel'),
      type: CONTROL_TYPES.RADIO,
      options: Object.values(EMPLOYEE_PAY_TYPE).map(value => ({ text: i18n.t(`common.payTypes.${value}`), value })),
    },
    {
      key: STREAM_FILTER_KEYS.PAY_SCHEDULE,
      label: i18n.t('employees.filters.payScheduleLabel'),
      type: CONTROL_TYPES.RADIO,
      options: payschedules.value.map(payschedule => ({
        text: payschedule.name,
        value: payschedule.pay_schedule_id,
      })),
      hidden: !payschedules.value.length,
    },
    {
      key: STREAM_FILTER_KEYS.DIVISION,
      label: i18n.t('employees.filters.divisionLabel'),
      type: CONTROL_TYPES.CHECKBOX,
      options: divisions.value.map(division => ({
        text: division.name,
        value: division.division_id,
      })),
      hidden: !divisions.value.length,
    },
  ].filter(filter => props.filterKeys.includes(filter.key)),
);

const componentType = computed(() => (props.nested ? 'div' : SmeCard));

const appliedApiFilters = computed(() => omitBy(pick(filters.value, STREAM_API_FILTER_KEYS), value => isNil(value)));

const filterConditions = computed(() => {
  const conditions = [];

  if (searchCriteria.value) {
    conditions.push(transfer => {
      return Object.values(pick(transfer, STREAM_SEARCH_FIELDS)).some(value =>
        value.toLowerCase().startsWith(searchCriteria.value.toLowerCase()),
      );
    });
  }

  if (filters.value[STREAM_FILTER_KEYS.SALARY_OR_HOURLY]) {
    conditions.push(
      transfer =>
        employeeFilters.value.salary_or_hourly?.[filters.value[STREAM_FILTER_KEYS.SALARY_OR_HOURLY]]?.includes(
          transfer?.employee_id,
        ) || false,
    );
  }

  if (filters.value[STREAM_FILTER_KEYS.DIVISION]?.length) {
    conditions.push(transfer => {
      return filters.value[STREAM_FILTER_KEYS.DIVISION].some(divisionFilter => {
        const divisionName = divisions.value.find(division => division.division_id === divisionFilter)?.name;

        return employeeFilters.value.division_name?.[divisionName]?.includes(transfer?.employee_id) || false;
      });
    });
  }

  return conditions;
});

const totalNetAmount = computed(() => toCurrency(sumBy(filteredTransfers.value, transfer => transfer.net_amount)));

watch(transfers, transfers => update(transfers.filter(filterFunction)));

const mapTransferPaySchedule = (transfer, map) => ({
  ...transfer,
  pay_schedule_name: map.get(transfer.properties?.pay_schedule_id) || map.get(transfer.pay_schedule_id),
});

const getTransfers = debounceAsync(async () => {
  const params = props.employeeId
    ? { ...appliedApiFilters.value, employee_id: props.employeeId }
    : appliedApiFilters.value;
  const rawTransfers = (await ApiClient.getTransfers(params))?.data || [];

  const paySchedulesMap = new Map(
    payschedules.value.map(payschedule => [payschedule.pay_schedule_id, payschedule.name]),
  );
  transfers.value = rawTransfers
    .map(transfer => mapTransferPaySchedule(transfer, paySchedulesMap))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}, 200);

const getDivisions = async () => (divisions.value = (await ApiClient.getDivisions())?.data || []);

const getEmployeeFilters = async () =>
  (employeeFilters.value = (await ApiClient.getFilters())?.filters_employees || {});

const update = transfers => {
  filteredTransfers.value = transfers;
  currentPage.value = 1;
};

const filterFunction = item =>
  filterConditions.value.length ? filterConditions.value.every(condition => condition(item)) : true;

const onSearch = () => (searchCriteria.value = searchText.value);

const exportFileRows = item =>
  fieldDefinitions.value.map(field =>
    field.formatter ? field.formatter(item[field.key], field.key, item) : item[field.key],
  );

onBeforeMount(async () => {
  watch(
    appliedApiFilters,
    async (newFilters, prevFilters) => {
      loading.value = true;
      error.value = '';

      try {
        if (!initialized.value) {
          await Promise.all([getTransfers(), getDivisions(), getPayschedules(), getEmployeeFilters()]);
        } else if (!isEqual(newFilters, prevFilters)) {
          await getTransfers();
        }
        // Success only: a failed init must stay uninitialised so the next filter change re-runs
        // the full Promise.all, not just the transfers refetch.
        initialized.value = true;
      } catch {
        error.value = i18n.t('transferredWages.loadErrorAlert');
      } finally {
        loading.value = false;
      }
    },
    {
      deep: true,
      immediate: true,
    },
  );
});
</script>
